import { createClient } from "@supabase/supabase-js";

// These will be replaced with actual values from your Supabase project
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Missing Supabase environment variables");
}

// Create Supabase client with proper headers
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  global: {
    headers: {
      Accept: "*/*", // Accept all content types, not just application/json
      "Content-Type": "application/json",
    },
  },
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

// Auth helper functions
export const signIn = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  return { data, error };
};

export const signUp = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
  });

  // If signup was successful, create a profile with the email
  if (!error && data?.user) {
    // Create or update the profile with the email
    const { error: profileError } = await supabase.from("profiles").upsert({
      id: data.user.id,
      email: email.toLowerCase(), // Store email in lowercase for easier matching
      full_name: null,
      avatar_url: null,
      updated_at: new Date().toISOString(),
    });

    if (profileError) {
      console.error("Error creating user profile:", profileError);
    }
  }

  return { data, error };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

export const getCurrentUser = async () => {
  console.log("supabase.ts - getCurrentUser called");

  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  console.log("supabase.ts - getUser result:", { userId: user?.id, error });

  if (user) {
    // Get profile data
    console.log("supabase.ts - Fetching profile for user:", user.id);
    const { data: profile } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", user.id)
      .single();

    console.log("supabase.ts - Profile data:", profile);

    if (profile) {
      // Merge profile data with user
      user.user_metadata = {
        ...user.user_metadata,
        ...profile,
      };
      console.log("supabase.ts - Merged user metadata:", user.user_metadata);
    }
  }

  return { user, error };
};

// Session management
export const onAuthStateChange = (
  callback: (event: any, session: any) => void
) => {
  return supabase.auth.onAuthStateChange(callback);
};

// Profile management
export const updateProfile = async (
  userId: string,
  updates: {
    full_name?: string;
    avatar_url?: string;
  }
) => {
  console.log("supabase.ts - updateProfile called with:", { userId, updates });

  try {
    const { data, error } = await supabase
      .from("profiles")
      .upsert({
        id: userId,
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) {
      console.error("supabase.ts - updateProfile error:", error);
    } else {
      console.log("supabase.ts - updateProfile success:", data);
    }

    return { data, error };
  } catch (err) {
    console.error("supabase.ts - updateProfile exception:", err);
    return {
      data: null,
      error:
        err instanceof Error
          ? err
          : new Error("Unknown error updating profile"),
    };
  }
};

export const uploadAvatar = async (userId: string, _file: File) => {
  // TODO: Add avatar upload logic later
  console.warn("Avatar upload not implemented yet");

  // Update the user profile with empty avatar URL
  const { data: profile, error } = await updateProfile(userId, {
    avatar_url: "", // placeholder
  });

  return { data: profile, error };
};
